import { useEffect, useState } from 'react';
import axios from 'axios';
import { BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, PieChart, Pie, Cell, LineChart, Line } from 'recharts';
import { Activity, CheckCircle2, Clock, AlertCircle, ChevronLeft, ChevronRight } from 'lucide-react';
import { toast } from 'sonner';
import FilterBar from '@/components/FilterBar';
import AIAnalysisPanel from '@/components/AIAnalysisPanel';
import { Button } from '@/components/ui/button';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

// Load filters from localStorage
const loadSavedFilters = () => {
  try {
    const saved = localStorage.getItem('dashboard_filters');
    if (saved) {
      return JSON.parse(saved);
    }
  } catch (e) {
    console.error('Error loading saved filters:', e);
  }
  return { project: '', category: '', user: '', status: '', includeResolved: false };
};

function Dashboard({ token }) {
  const [analytics, setAnalytics] = useState(null);
  const [trends, setTrends] = useState(null);
  const [issues, setIssues] = useState([]);
  const [filteredIssues, setFilteredIssues] = useState([]);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [aiPanelOpen, setAiPanelOpen] = useState(false);
  const [filters, setFilters] = useState(loadSavedFilters());
  
  // Projects, categories, users, statuses loaded separately
  const [allProjects, setAllProjects] = useState([]);
  const [allCategories, setAllCategories] = useState([]);
  const [allUsers, setAllUsers] = useState([]);
  const [allStatuses, setAllStatuses] = useState([]);
  
  // Pagination state
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [totalIssues, setTotalIssues] = useState(0);
  const pageSize = 100;

  const COLORS = {
    primary: 'rgb(59, 130, 246)',
    accent: 'rgb(16, 185, 129)',
    warning: 'rgb(245, 158, 11)',
    destructive: 'rgb(239, 68, 68)',
    secondary: 'rgb(161, 161, 170)',
  };

  // Save filters to localStorage when they change
  useEffect(() => {
    localStorage.setItem('dashboard_filters', JSON.stringify(filters));
  }, [filters]);

  useEffect(() => {
    fetchData();
  }, [token]);

  useEffect(() => {
    if (!loading) {
      setCurrentPage(1); // Reset to page 1 when filters change
      fetchFilteredIssues();
    }
  }, [filters]);

  const fetchData = async () => {
    setLoading(true);
    try {
      // Load projects first to populate filter options
      const projectsResponse = await axios.get(`${BACKEND_URL}/api/projects`, {
        headers: { Authorization: `Bearer ${token}` },
        timeout: 30000,
      });
      const projectNames = (projectsResponse.data.projects || []).map(p => p.name).sort();
      setAllProjects(projectNames);

      // Sequential to avoid timeout issues
      const analyticsParams = new URLSearchParams();
      if (filters.includeResolved) {
        analyticsParams.append('include_resolved', 'true');
      }
      
      const analyticsResponse = await axios.get(`${BACKEND_URL}/api/analytics/overview?${analyticsParams.toString()}`, {
        headers: { Authorization: `Bearer ${token}` },
        timeout: 90000,
      });
      setAnalytics(analyticsResponse.data);
      
      const trendsResponse = await axios.get(`${BACKEND_URL}/api/analytics/trends?${analyticsParams.toString()}`, {
        headers: { Authorization: `Bearer ${token}` },
        timeout: 90000,
      });
      setTrends(trendsResponse.data);
      
      // Fetch issues without filters initially
      await fetchFilteredIssues();
    } catch (error) {
      toast.error(`Errore nel caricamento: ${error.message}`);
      console.error('Fetch error:', error);
    } finally {
      setLoading(false);
    }
  };

  const fetchFilteredIssues = async (page = currentPage, updateFilterOptions = false) => {
    setRefreshing(true);
    try {
      const params = new URLSearchParams();
      params.append('page', page.toString());
      params.append('page_size', pageSize.toString());
      
      // Add includeResolved flag (default is false = stato < 80)
      if (filters.includeResolved) {
        params.append('include_resolved', 'true');
      }
      
      // Add filters, convert 'all' to empty
      if (filters.status && filters.status !== 'all') {
        params.append('filter_state', filters.status);
      }
      if (filters.project && filters.project !== 'all') {
        params.append('filter_project', filters.project);
      }
      if (filters.category && filters.category !== 'all') {
        params.append('filter_category', filters.category);
      }
      if (filters.user && filters.user !== 'all') {
        params.append('filter_user', filters.user);
      }

      const issuesResponse = await axios.get(`${BACKEND_URL}/api/issues?${params.toString()}`, {
        headers: { Authorization: `Bearer ${token}` },
        timeout: 90000,
      });
      
      const issuesData = issuesResponse.data.issues || [];
      setIssues(issuesData);
      setFilteredIssues(issuesData);
      setTotalPages(issuesResponse.data.total_pages || 1);
      setTotalIssues(issuesResponse.data.total || 0);
      setCurrentPage(page);
      
      // Update filter options from first fetch (unfiltered data) or when requested
      if (updateFilterOptions || allCategories.length === 0) {
        const categories = [...new Set(issuesData.map(i => i.category?.name).filter(Boolean))].sort();
        const users = [...new Set(issuesData.map(i => i.handler?.real_name || i.handler?.name).filter(Boolean))].sort();
        const statuses = [...new Set(issuesData.map(i => i.status?.name).filter(Boolean))].sort();
        setAllCategories(categories);
        setAllUsers(users);
        setAllStatuses(statuses);
      }
    } catch (error) {
      toast.error(`Errore nel caricamento issues: ${error.message}`);
      console.error('Issues fetch error:', error);
    } finally {
      setRefreshing(false);
    }
  };

  const handlePageChange = (newPage) => {
    if (newPage >= 1 && newPage <= totalPages) {
      fetchFilteredIssues(newPage);
    }
  };

  const handleFilterChange = (newFilters) => {
    setFilters(newFilters);
  };

  const handleRefresh = async () => {
    setRefreshing(true);
    try {
      // Reload analytics and trends
      const analyticsResponse = await axios.get(`${BACKEND_URL}/api/analytics/overview`, {
        headers: { Authorization: `Bearer ${token}` },
        timeout: 90000,
      });
      setAnalytics(analyticsResponse.data);
      
      const trendsResponse = await axios.get(`${BACKEND_URL}/api/analytics/trends`, {
        headers: { Authorization: `Bearer ${token}` },
        timeout: 90000,
      });
      setTrends(trendsResponse.data);
      
      // Reload filtered issues
      await fetchFilteredIssues();
      
      toast.success('Dati aggiornati con successo!');
    } catch (error) {
      toast.error(`Errore nell'aggiornamento: ${error.message}`);
      console.error('Refresh error:', error);
    } finally {
      setRefreshing(false);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-96" data-testid="loading-spinner">
        <div className="text-[rgb(161,161,170)] font-mono">Caricamento...</div>
      </div>
    );
  }

  const statusData = Object.entries(analytics?.by_status || {}).map(([name, value]) => ({
    name,
    value,
  }));

  const priorityData = Object.entries(analytics?.by_priority || {}).map(([name, value]) => ({
    name,
    value,
  }));

  const userTaskData = Object.entries(analytics?.by_user || {})
    .map(([name, value]) => ({ name, tasks: value }))
    .sort((a, b) => b.tasks - a.tasks)
    .slice(0, 10);

  const trendData = Object.entries(trends?.daily_created || {})
    .map(([date, created]) => ({
      date,
      created,
      resolved: trends?.daily_resolved?.[date] || 0,
    }))
    .sort((a, b) => a.date.localeCompare(b.date))
    .slice(-30);

  return (
    <div className="space-y-6" data-testid="dashboard-overview">
      <div className="mb-8">
        <h1 className="text-4xl font-bold font-mono mb-2" data-testid="dashboard-title">
          Dashboard Overview
        </h1>
        <p className="text-[rgb(161,161,170)] text-sm font-mono uppercase tracking-widest">
          Real-time metrics & analytics
        </p>
      </div>

      <FilterBar
        projects={allProjects}
        categories={allCategories}
        users={allUsers}
        statuses={allStatuses}
        onFilterChange={handleFilterChange}
        onRefresh={handleRefresh}
        isRefreshing={refreshing}
        onAIAnalysis={() => setAiPanelOpen(true)}
        initialFilters={filters}
      />

      {/* KPI Cards */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-4" data-testid="kpi-cards">
        <div className="bg-[rgb(24,24,27)]/50 border border-[rgb(39,39,42)]/50 p-6 rounded-md backdrop-blur-sm" data-testid="kpi-total-issues">
          <div className="flex items-center justify-between mb-4">
            <span className="text-[rgb(161,161,170)] text-xs uppercase tracking-widest font-mono">
              Total Issues
            </span>
            <Activity className="h-5 w-5 text-[rgb(59,130,246)]" />
          </div>
          <div className="text-5xl font-bold font-mono text-[rgb(250,250,250)]">
            {refreshing ? <span className="text-2xl animate-pulse">...</span> : totalIssues}
          </div>
          {totalPages > 1 && (
            <div className="text-xs text-[rgb(161,161,170)] mt-2 font-mono">
              Pag. {currentPage}/{totalPages}
            </div>
          )}
        </div>

        <div className="bg-[rgb(24,24,27)]/50 border border-[rgb(39,39,42)]/50 p-6 rounded-md backdrop-blur-sm" data-testid="kpi-open-issues">
          <div className="flex items-center justify-between mb-4">
            <span className="text-[rgb(161,161,170)] text-xs uppercase tracking-widest font-mono">
              Open
            </span>
            <Clock className="h-5 w-5 text-[rgb(245,158,11)]" />
          </div>
          <div className="text-5xl font-bold font-mono text-[rgb(245,158,11)]">
            {refreshing ? <span className="text-2xl animate-pulse">...</span> : (filteredIssues || []).filter(i => !['resolved', 'closed'].some(s => i.status?.name?.toLowerCase().includes(s))).length}
          </div>
        </div>

        <div className="bg-[rgb(24,24,27)]/50 border border-[rgb(39,39,42)]/50 p-6 rounded-md backdrop-blur-sm" data-testid="kpi-resolved-issues">
          <div className="flex items-center justify-between mb-4">
            <span className="text-[rgb(161,161,170)] text-xs uppercase tracking-widest font-mono">
              Resolved
            </span>
            <CheckCircle2 className="h-5 w-5 text-[rgb(16,185,129)]" />
          </div>
          <div className="text-5xl font-bold font-mono text-[rgb(16,185,129)]">
            {refreshing ? <span className="text-2xl animate-pulse">...</span> : (filteredIssues || []).filter(i => ['resolved', 'closed'].some(s => i.status?.name?.toLowerCase().includes(s))).length}
          </div>
        </div>

        <div className="bg-[rgb(24,24,27)]/50 border border-[rgb(39,39,42)]/50 p-6 rounded-md backdrop-blur-sm" data-testid="kpi-resolution-rate">
          <div className="flex items-center justify-between mb-4">
            <span className="text-[rgb(161,161,170)] text-xs uppercase tracking-widest font-mono">
              Resolution Rate
            </span>
            <AlertCircle className="h-5 w-5 text-[rgb(59,130,246)]" />
          </div>
          <div className="text-5xl font-bold font-mono text-[rgb(250,250,250)]">
            {refreshing ? <span className="text-2xl animate-pulse">...</span> : (
              <>
                {(filteredIssues || []).length
                  ? Math.round(((filteredIssues || []).filter(i => ['resolved', 'closed'].some(s => i.status?.name?.toLowerCase().includes(s))).length / (filteredIssues || []).length) * 100)
                  : 0}
                %
              </>
            )}
          </div>
        </div>
      </div>

      {/* Pagination Controls */}
      {totalPages > 1 && (
        <div className="flex items-center justify-center gap-4 py-4">
          <Button
            variant="outline"
            size="sm"
            onClick={() => handlePageChange(currentPage - 1)}
            disabled={currentPage <= 1 || refreshing}
            className="font-mono"
          >
            <ChevronLeft className="h-4 w-4 mr-1" />
            Precedente
          </Button>
          <span className="text-sm font-mono text-[rgb(161,161,170)]">
            Pagina {currentPage} di {totalPages} ({totalIssues} issue totali)
          </span>
          <Button
            variant="outline"
            size="sm"
            onClick={() => handlePageChange(currentPage + 1)}
            disabled={currentPage >= totalPages || refreshing}
            className="font-mono"
          >
            Successiva
            <ChevronRight className="h-4 w-4 ml-1" />
          </Button>
        </div>
      )}

      {/* Charts Grid */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Status Distribution */}
        <div className="bg-[rgb(24,24,27)]/50 border border-[rgb(39,39,42)]/50 p-6 rounded-md backdrop-blur-sm" data-testid="status-chart">
          <div className="border-b border-[rgb(39,39,42)]/50 pb-4 mb-4">
            <h3 className="text-xl font-mono font-bold">Status Distribution</h3>
            <p className="text-[rgb(161,161,170)] text-xs uppercase tracking-widest font-mono mt-1">
              Issues by status
            </p>
          </div>
          <ResponsiveContainer width="100%" height={300}>
            <PieChart>
              <Pie
                data={statusData}
                cx="50%"
                cy="50%"
                labelLine={false}
                label={({ name, percent }) => `${name}: ${(percent * 100).toFixed(0)}%`}
                outerRadius={100}
                fill="#8884d8"
                dataKey="value"
              >
                {statusData.map((entry, index) => (
                  <Cell key={`cell-${index}`} fill={Object.values(COLORS)[index % Object.values(COLORS).length]} />
                ))}
              </Pie>
              <Tooltip
                contentStyle={{
                  backgroundColor: 'rgb(24, 24, 27)',
                  border: '1px solid rgb(39, 39, 42)',
                  borderRadius: '0.375rem',
                  fontFamily: 'JetBrains Mono, monospace',
                }}
              />
            </PieChart>
          </ResponsiveContainer>
        </div>

        {/* Priority Distribution */}
        <div className="bg-[rgb(24,24,27)]/50 border border-[rgb(39,39,42)]/50 p-6 rounded-md backdrop-blur-sm" data-testid="priority-chart">
          <div className="border-b border-[rgb(39,39,42)]/50 pb-4 mb-4">
            <h3 className="text-xl font-mono font-bold">Priority Distribution</h3>
            <p className="text-[rgb(161,161,170)] text-xs uppercase tracking-widest font-mono mt-1">
              Issues by priority
            </p>
          </div>
          <ResponsiveContainer width="100%" height={300}>
            <BarChart data={priorityData}>
              <CartesianGrid strokeDasharray="3 3" stroke="rgb(39, 39, 42)" />
              <XAxis
                dataKey="name"
                stroke="rgb(161, 161, 170)"
                style={{ fontFamily: 'JetBrains Mono', fontSize: '12px' }}
              />
              <YAxis stroke="rgb(161, 161, 170)" style={{ fontFamily: 'JetBrains Mono', fontSize: '12px' }} />
              <Tooltip
                contentStyle={{
                  backgroundColor: 'rgb(24, 24, 27)',
                  border: '1px solid rgb(39, 39, 42)',
                  borderRadius: '0.375rem',
                  fontFamily: 'JetBrains Mono, monospace',
                }}
              />
              <Bar dataKey="value" fill={COLORS.primary} />
            </BarChart>
          </ResponsiveContainer>
        </div>

        {/* User Task Distribution */}
        <div className="bg-[rgb(24,24,27)]/50 border border-[rgb(39,39,42)]/50 p-6 rounded-md backdrop-blur-sm" data-testid="user-tasks-chart">
          <div className="border-b border-[rgb(39,39,42)]/50 pb-4 mb-4">
            <h3 className="text-xl font-mono font-bold">Top 10 Users by Tasks</h3>
            <p className="text-[rgb(161,161,170)] text-xs uppercase tracking-widest font-mono mt-1">
              Task assignment overview
            </p>
          </div>
          <ResponsiveContainer width="100%" height={300}>
            <BarChart data={userTaskData} layout="vertical">
              <CartesianGrid strokeDasharray="3 3" stroke="rgb(39, 39, 42)" />
              <XAxis type="number" stroke="rgb(161, 161, 170)" style={{ fontFamily: 'JetBrains Mono', fontSize: '12px' }} />
              <YAxis
                dataKey="name"
                type="category"
                stroke="rgb(161, 161, 170)"
                style={{ fontFamily: 'JetBrains Mono', fontSize: '10px' }}
                width={120}
              />
              <Tooltip
                contentStyle={{
                  backgroundColor: 'rgb(24, 24, 27)',
                  border: '1px solid rgb(39, 39, 42)',
                  borderRadius: '0.375rem',
                  fontFamily: 'JetBrains Mono, monospace',
                }}
              />
              <Bar dataKey="tasks" fill={COLORS.accent} />
            </BarChart>
          </ResponsiveContainer>
        </div>

        {/* Trend Chart */}
        <div className="bg-[rgb(24,24,27)]/50 border border-[rgb(39,39,42)]/50 p-6 rounded-md backdrop-blur-sm" data-testid="trends-chart">
          <div className="border-b border-[rgb(39,39,42)]/50 pb-4 mb-4">
            <h3 className="text-xl font-mono font-bold">Trend Analysis (30 Days)</h3>
            <p className="text-[rgb(161,161,170)] text-xs uppercase tracking-widest font-mono mt-1">
              Created vs Resolved
            </p>
          </div>
          <ResponsiveContainer width="100%" height={300}>
            <LineChart data={trendData}>
              <CartesianGrid strokeDasharray="3 3" stroke="rgb(39, 39, 42)" />
              <XAxis
                dataKey="date"
                stroke="rgb(161, 161, 170)"
                style={{ fontFamily: 'JetBrains Mono', fontSize: '10px' }}
              />
              <YAxis stroke="rgb(161, 161, 170)" style={{ fontFamily: 'JetBrains Mono', fontSize: '12px' }} />
              <Tooltip
                contentStyle={{
                  backgroundColor: 'rgb(24, 24, 27)',
                  border: '1px solid rgb(39, 39, 42)',
                  borderRadius: '0.375rem',
                  fontFamily: 'JetBrains Mono, monospace',
                }}
              />
              <Line type="monotone" dataKey="created" stroke={COLORS.primary} strokeWidth={2} name="Created" />
              <Line type="monotone" dataKey="resolved" stroke={COLORS.accent} strokeWidth={2} name="Resolved" />
            </LineChart>
          </ResponsiveContainer>
        </div>
      </div>

      <AIAnalysisPanel
        issues={filteredIssues}
        context="Dashboard Overview - Analisi generale di tutte le issue"
        token={token}
        isOpen={aiPanelOpen}
        onClose={() => setAiPanelOpen(false)}
      />
    </div>
  );
}

export default Dashboard;
